import { useLocation } from 'react-router-dom';
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export type EntityKind = 'person' | 'account' | 'team';

export interface Point { x: number; y: number; }

export interface OpenState {
  coverageRepPid?: string | undefined;
  kind: EntityKind;
  id: string;
  rect: DOMRect;
  pinned: boolean;
  readOnlyOnHover?: boolean;
  /** Viewport coords of the gesture that opened the panel (cursor). The
   *  prototype anchors the detail panel at the cursor, not the card edge;
   *  null for keyboard opens (falls back to the anchor rect). */
  point: Point | null;
  /** Element that opened a pinned panel, used to restore keyboard focus. */
  trigger: HTMLElement | null;
}

interface PopoverCtx {
  open: OpenState | null;
  show: (
    kind: EntityKind,
    id: string,
    rect: DOMRect,
    pinned: boolean,
    point?: Point | null,
    trigger?: HTMLElement | null,
    readOnlyOnHover?: boolean,
    coverageRepPid?: string | undefined,
  ) => void;
  close: () => void;
  /** Start the hover-out hide timer. No-op when the popover is pinned. */
  scheduleClose: () => void;
  /** Cancel a pending hover-out hide. Called on re-enter (trigger or host). */
  cancelClose: () => void;
}

const Ctx = createContext<PopoverCtx | null>(null);

const HIDE_DELAY_MS = 250; // prototype cancelHoverPreview closeTimer

export function PopoverProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<OpenState | null>(null);
  const hideTimer = useRef<number | null>(null);
  const openRef = useRef<OpenState | null>(null);
  const restoredFocus = useRef<HTMLElement | null>(null);
  openRef.current = open;

  const cancelClose = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelClose();
    restoredFocus.current = openRef.current?.pinned ? openRef.current.trigger : null;
    setOpen(null);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    if (openRef.current?.pinned) return;
    hideTimer.current = window.setTimeout(() => {
      hideTimer.current = null;
      setOpen((cur) => (cur && !cur.pinned ? null : cur));
    }, HIDE_DELAY_MS);
  }, [cancelClose]);

  const show = useCallback<PopoverCtx['show']>(
    (kind, id, rect, pinned, point = null, trigger = null, readOnlyOnHover = false, coverageRepPid) => {
      cancelClose();
      if (!pinned && !point && trigger && restoredFocus.current === trigger) {
        restoredFocus.current = null;
        return;
      }
      restoredFocus.current = null;
      setOpen((prev) => {
        // If we're showing the same entity, preserve pinned state from a click
        // (don't let a hover demote a click-pinned popover).
        if (prev && prev.kind === kind && prev.id === id) {
          return {
            coverageRepPid,
            kind,
            id,
            rect,
            pinned: prev.pinned || pinned,
            readOnlyOnHover,
            point,
            trigger: trigger ?? prev.trigger,
          };
        }
        // A pinned panel is NOT replaced by a hover-preview of a DIFFERENT entity
        // (prototype startHoverPreview: `if (panelPinned) return`). Only an
        // explicit click / switchTo (pinned === true) or an explicit close may
        // replace it — otherwise a mere mouse pass-over silently destroys the
        // card the user pinned to interact with.
        if (prev && prev.pinned && !pinned) {
          return prev;
        }
        return { kind, id, rect, pinned, readOnlyOnHover, point, coverageRepPid, trigger: trigger ?? prev?.trigger ?? null };
      });
    },
    [cancelClose],
  );

  const value = useMemo<PopoverCtx>(
    () => ({ open, show, close, scheduleClose, cancelClose }),
    [open, show, close, scheduleClose, cancelClose],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePopover() {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePopover must be used inside <PopoverProvider>');
  return v;
}

const PopoverHost = lazy(() => import('./EntityPopover').then(module => ({ default: module.EntityPopoverHost })));

export function DeferredEntityPopoverHost() {
  const { open, close } = usePopover();
  const { pathname } = useLocation();
  // Close on navigation. The panel is a portal that outlives a route change —
  // "View detail →" and the nav menu route away while it's mounted, so without
  // this it stays open over the destination page and intercepts clicks where it
  // overlaps (e.g. account detail's "Save note"). A full reload destroys it;
  // SPA navigation does not. Covers detail links, nav items, and back/forward.
  useEffect(() => {
    close();
  }, [pathname, close]);
  const [activated, setActivated] = useState(false);
  useEffect(() => { if (open) setActivated(true); }, [open]);
  return open || activated ? <Suspense fallback={null}><PopoverHost /></Suspense> : null;
}
